/**
 * Organization Layer Component
 * Bottom layer with Collections tabs
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Folder, Plus, Loader, Grid3x3, Trash2, ChevronRight, X, CheckSquare, Square } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import topicService from '../../services/topicService';
import contentService from '../../services/contentService';
import MasonryGrid from './MasonryGrid';

import { Clock } from 'lucide-react';
import RecentlyViewedItems from './RecentlyViewedItems';

const TABS = [
  { id: 'all', label: 'All', icon: Grid3x3 },
  { id: 'recent', label: 'Recently Viewed', icon: Clock },
  { id: 'collections', label: 'Collections', icon: Folder },
];

export default function OrganizationLayer({ activeTab = 'all', onSwirlClick, onCollectionClick, items = [], totalItemCount = null, chips = [], loading = false, onFilterClick, onItemClick, onItemDelete, searchMeta = null, activeFilter = null }) {
  const { user } = useAuth();
  // activeTab now controlled by parent
  const [collections, setCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteMode, setDeleteMode] = useState('collection'); // 'collection' or 'items'
  const [deletingCollection, setDeletingCollection] = useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = useState(null); // Track which collection is selected
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showBulkCollectionSelector, setShowBulkCollectionSelector] = useState(false);
  const [changingBulkCollection, setChangingBulkCollection] = useState(false);

  // Listen for tab switch events from outside
  useEffect(() => {
    const handleTabSwitch = (event) => {
      if (event.detail && ['all', 'recent', 'collections'].includes(event.detail)) {
        setActiveTab(event.detail);
        
        // Clear active filters when switching to All tab
        if (event.detail === 'all') {
          window.dispatchEvent(new CustomEvent('clearActiveFilters'));
        }
      }
    };
    
    window.addEventListener('organization:switchTab', handleTabSwitch);
    return () => {
      window.removeEventListener('organization:switchTab', handleTabSwitch);
    };
  }, []);

  // Load collections
  useEffect(() => {
    if (user?.id && activeTab === 'collections') {
      loadCollections();
    }
  }, [user?.id, activeTab]);
  
  // Listen for collection changes to refresh counts
  useEffect(() => {
    const handleCollectionChanged = () => {
      if (activeTab === 'collections') {
        console.log('🔄 [OrganizationLayer] Collection changed, refreshing counts...');
        loadCollections();
      }
    };
    
    window.addEventListener('contentCollectionChanged', handleCollectionChanged);
    return () => {
      window.removeEventListener('contentCollectionChanged', handleCollectionChanged);
    };
  }, [activeTab, user?.id]);

  const loadCollections = async () => {
    if (!user?.id) {
      setCollections([]);
      setLoadingCollections(false);
      return;
    }

    setLoadingCollections(true);
    try {
      const { data, error } = await topicService.getUserTopics(user.id);
      if (error) {
        console.error('Error loading collections:', error);
        setCollections([]);
      } else {
        // Filter out any invalid/null collections and ensure they have required fields
        const validCollections = (data || []).filter(c => c && c.id && c.name);
        
        // Refresh content counts for all collections (optimized - load deepsearch.items once)
        console.log('🔄 [OrganizationLayer] Refreshing content counts for', validCollections.length, 'collections');
        
        // Use topic_items join table for counts (source of truth)
        const { data: topicItemsData, error: topicItemsError } = await supabase
          .schema('public')
          .from('topic_items')
          .select('topic_id')
          .eq('user_id', user.id);
        
        if (topicItemsError) {
          console.error('❌ Error loading topic_items for counts:', topicItemsError);
        }
        
        // Create a map of topic_id -> count for fast lookup
        const itemCountsByCollection = new Map();
        (topicItemsData || []).forEach(row => {
          const topicId = row.topic_id;
          if (topicId) {
            itemCountsByCollection.set(topicId, (itemCountsByCollection.get(topicId) || 0) + 1);
          }
        });
        
        console.log(`📦 [OrganizationLayer] Loaded ${topicItemsData?.length || 0} topic_items rows, found ${itemCountsByCollection.size} collections with items`);
        
        // First, just get counts (fast)
        const collectionsWithCounts = await Promise.all(
          validCollections.map(async (collection) => {
            try {
              // Special case: "All" collection shows ALL items from deepsearch.items
              if (collection.name === 'All') {
                const { count, error: countError } = await supabase
                  .schema('deepsearch')
                  .from('items')
                  .select('*', { count: 'exact', head: true })
                  .eq('user_id', user.id)
                  .not('url', 'like', 'pending://%');
                
                const allCount = count || 0;
                console.log(`📊 [OrganizationLayer] Collection "All" has ${allCount} items from deepsearch.items`);
                return { ...collection, content_count: allCount, recent_thumbnail: null };
              }
              
              // Use topic_items join table for counts
              const contentCount = itemCountsByCollection.get(collection.id) || 0;
              
              console.log(`📊 [OrganizationLayer] Collection "${collection.name}" has ${contentCount} items from topic_items`);
              return { ...collection, content_count: contentCount, recent_thumbnail: null };
            } catch (error) {
              console.error(`❌ Failed to get content count for collection ${collection.id}:`, error);
              return { ...collection, content_count: 0, recent_thumbnail: null };
            }
          })
        );
        
        // Add cached thumbnails from database (instant - single query)
        const collectionIds = collectionsWithCounts.map(c => c.id);
        const { data: thumbnailData } = await supabase
          .schema('public')
          .from('topics')
          .select('id, recent_thumbnail')
          .in('id', collectionIds);
        
        // Create thumbnail map
        const thumbnailMap = {};
        (thumbnailData || []).forEach(t => {
          thumbnailMap[t.id] = t.recent_thumbnail;
        });
        
        // Add thumbnails to collections
        const collectionsWithThumbnails = collectionsWithCounts.map(c => ({
          ...c,
          recent_thumbnail: thumbnailMap[c.id] || null
        }));
        
        console.log('✅ [OrganizationLayer] Added cached thumbnails (instant!)');
        
        // Sort collections: "All" first, then alphabetically
        collectionsWithThumbnails.sort((a, b) => {
          if (a.name === 'All') return -1;
          if (b.name === 'All') return 1;
          return a.name.localeCompare(b.name);
        });
        
        setCollections(collectionsWithThumbnails);
        if (validCollections.length !== (data || []).length) {
          console.warn(`Filtered out ${(data || []).length - validCollections.length} invalid collections`);
        }
        console.log('✅ [OrganizationLayer] Finished refreshing collection counts');
      }
    } catch (error) {
      console.error('Error loading collections:', error);
      setCollections([]);
    } finally {
      setLoadingCollections(false);
    }
  };

  const handleCreateCollection = async () => {
    if (!newCollectionName.trim() || !user?.id) return;

    setCreatingCollection(true);
    try {
      const topicData = {
        user_id: user.id,
        name: newCollectionName.trim(),
        description: '',
        color: topicService.getRandomColor(),
      };

      const { data, error } = await topicService.createTopic(topicData);
      if (error) {
        console.error('Error creating collection:', error);
        alert('Failed to create collection. Please try again.');
      } else {
        setNewCollectionName('');
        setShowCreateModal(false);
        // Refresh collections
        await loadCollections();
        // Stay on Collections tab - don't navigate away
        // Don't call onCollectionClick - keep user on Collections tab
      }
    } catch (error) {
      console.error('Error creating collection:', error);
      alert('Failed to create collection. Please try again.');
    } finally {
      setCreatingCollection(false);
    }
  };

  const handleCollectionSelect = (collection) => {
    if (!collection || !collection.id) {
      console.error('Invalid collection selected:', collection);
      return;
    }
    // Set selected collection immediately to trigger UI update
    setSelectedCollectionId(collection.id);
    
    // Call the handler to load collection items
    // The handler will clear items first, so we'll see loading state
    if (onCollectionClick) {
      try {
        onCollectionClick(collection);
      } catch (error) {
        console.error('Error in onCollectionClick:', error);
      }
    }
  };
  
  const handleBackToCollections = () => {
    setSelectedCollectionId(null);
    // Clear active collection filter
    if (window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('clearActiveFilters'));
    }
  };

  const handleDeleteCollection = (collection, e) => {
    e.stopPropagation(); // Prevent collection selection
    setCollectionToDelete(collection);
    setShowDeleteModal(true);
    setDeleteConfirmText('');
    setDeleteMode('collection'); // Default to collection only
  };

  const handleBulkChangeCollection = async (collectionId) => {
    if (!user?.id || selectedItems.size === 0 || changingBulkCollection) return;

    setChangingBulkCollection(true);
    try {
      const itemIds = Array.from(selectedItems);
      
      // Separate items by source (deepsearch.items vs instagram_content)
      const deepsearchItems = items.filter(item => 
        itemIds.includes(item.id) && 
        (item._source === 'deepsearch.items' || item.source_type === 'image' || item.source_type === 'upload')
      );
      const instagramItems = items.filter(item => 
        itemIds.includes(item.id) && 
        !deepsearchItems.some(di => di.id === item.id)
      );

      // Update deepsearch.items (store in meta.collection_id)
      if (deepsearchItems.length > 0) {
        const deepsearchIds = deepsearchItems.map(item => item.id);
        
        // Fetch current meta for all items
        const { data: currentItems, error: fetchError } = await supabase
          .schema('deepsearch')
          .from('items')
          .select('id, meta')
          .eq('user_id', user.id)
          .in('id', deepsearchIds);

        if (fetchError) {
          console.error('Failed to fetch items for bulk update:', fetchError);
          throw fetchError;
        }

        // Update each item's meta
        const updatePromises = (currentItems || []).map(async (item) => {
          const currentMeta = item.meta || {};
          const updatedMeta = {
            ...currentMeta,
            collection_id: collectionId,
          };

          const { error } = await supabase
            .schema('deepsearch')
            .from('items')
            .update({ meta: updatedMeta })
            .eq('id', item.id)
            .eq('user_id', user.id);

          if (error) {
            console.error(`Failed to update item ${item.id}:`, error);
            throw error;
          }
        });

        await Promise.all(updatePromises);
        console.log(`✅ Updated ${deepsearchItems.length} deepsearch items`);
      }

      // Update instagram_content (use topic_id)
      if (instagramItems.length > 0) {
        const instagramIds = instagramItems.map(item => item.id);
        
        const { error } = await supabase
          .schema('public')
          .from('instagram_content')
          .update({ topic_id: collectionId })
          .eq('user_id', user.id)
          .in('id', instagramIds);

        if (error) {
          console.error('Failed to update instagram_content:', error);
          throw error;
        }
        console.log(`✅ Updated ${instagramItems.length} instagram items`);
      }

      // Clear selection and exit bulk mode
      setSelectedItems(new Set());
      setBulkSelectMode(false);
      setShowBulkCollectionSelector(false);

      // Dispatch event to refresh parent components
      window.dispatchEvent(new CustomEvent('contentCollectionChanged', {
        detail: { itemIds, collectionId },
      }));

      // Reload collections to refresh counts
      await loadCollections();

      // If we're viewing a collection, refresh items
      if (selectedCollectionId) {
        // Trigger a refresh by dispatching an event that the parent can listen to
        window.dispatchEvent(new CustomEvent('refreshCollectionItems'));
      }

    } catch (error) {
      console.error('Failed to change collection:', error);
      alert(`Failed to change collection: ${error.message || 'Please try again.'}`);
    } finally {
      setChangingBulkCollection(false);
    }
  };

  const confirmDeleteCollection = async () => {
    if (!collectionToDelete || !user?.id) return;
    
    // Validate confirmation text - must type "delete"
    if (deleteConfirmText.trim().toLowerCase() !== 'delete') {
      alert('Please type "delete" to confirm.');
      return;
    }

    setDeletingCollection(true);
    try {
      if (deleteMode === 'items') {
        // Get all content in this collection (topics)
        try {
          const { data: collectionContent, error: contentError } = await supabase
            .schema('public')
            .from('instagram_content')
            .select('id')
            .eq('user_id', user.id)
            .eq('topic_id', collectionToDelete.id);
          
          if (contentError) {
            console.error('Error fetching collection content:', contentError);
          } else if (collectionContent && collectionContent.length > 0) {
            // Delete all items first
            console.log(`🗑️ Deleting ${collectionContent.length} items from collection...`);
            for (const content of collectionContent) {
              const { error } = await contentService.deleteContent(user.id, content.id);
              if (error) {
                console.error(`Failed to delete content ${content.id}:`, error);
                // Continue deleting others even if one fails
              }
            }
            console.log(`✅ Deleted ${collectionContent.length} items from collection`);
          }
        } catch (err) {
          console.error('Error deleting collection items:', err);
          // Continue with collection deletion even if item deletion fails
        }
      }
      
      // Delete the collection itself
      const { error } = await topicService.deleteTopic(collectionToDelete.id);
      if (error) throw error;
      
      // Remove from local state
      setCollections(prev => prev.filter(c => c.id !== collectionToDelete.id));
      
      // Close modal
      setShowDeleteModal(false);
      setCollectionToDelete(null);
      setDeleteConfirmText('');
      
      // Refresh collections
      await loadCollections();
    } catch (error) {
      console.error('Failed to delete collection:', error);
      alert(`Failed to delete collection: ${error.message || 'Unknown error'}`);
    } finally {
      setDeletingCollection(false);
    }
  };

  return (
    <div id="organization-layer" className="mt-0 relative" style={{ zIndex: 1 }}>
      {/* Tabs moved to header - hidden here */}

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'recent' ? (
          <RecentlyViewedItems
            key="recent-tab-content"
            onItemClick={onItemClick}
            onItemDelete={onItemDelete}
          />
        ) : activeTab === 'all' ? (
          <div className="relative z-20" key="all-tab-content">
            {/* All tab shows user's saved and uploaded items only (no AI recommendations) */}
            
            {/* Sort info only */}
            {items && items.length > 0 && (
              <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <span>Showing newest first</span>
                  <span className="text-neutral-400">·</span>
                  <span>
                    {totalItemCount !== null
                      ? `${totalItemCount} ${totalItemCount === 1 ? 'item' : 'items'}`
                      : `${items.length} ${items.length === 1 ? 'item' : 'items'}`
                    }
                  </span>
                </div>
              </div>
            )}
            
            {loading && items.length === 0 ? (
              <div className="mb-6 text-neutral-500 text-center py-12 relative z-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A47F5] mx-auto mb-4"></div>
                <p>Searching…</p>
              </div>
            ) : items && items.length > 0 ? (
              <>
                <MasonryGrid items={items} onItemClick={onItemClick} onItemDelete={onItemDelete} searchMeta={searchMeta} />
                <div id="scroll-sentinel" className="h-20 flex items-center justify-center relative z-20">
                  {loading && items.length > 0 && (
                    <div className="text-neutral-500 text-sm">Loading more…</div>
                  )}
                </div>
              </>
            ) : activeFilter === 'theme' ? (
              // Theme filter has its own empty state in parent component
              null
            ) : (
              <div className="text-center py-12 text-neutral-500 relative z-20">
                <p className="text-lg mb-2">No results found.</p>
                <p>Try different filters or search terms.</p>
              </div>
            )}
          </div>
        ) : activeTab === 'collections' ? (
          <div key="collections">
            {selectedCollectionId ? (
              // Show collection items when a collection is selected
              <div>
                {/* Back button and collection header */}
                <div className="flex items-center gap-4 mb-6">
                  <button
                    onClick={handleBackToCollections}
                    className="flex items-center gap-2 px-4 py-2 text-neutral-600 hover:text-neutral-800 hover:bg-white/50 rounded-lg transition-all"
                  >
                    <ChevronRight className="w-4 h-4 rotate-180" />
                    <span>Back to Collections</span>
                  </button>
                  {(() => {
                    const selectedCollection = collections.find(c => c.id === selectedCollectionId);
                    return selectedCollection && (
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg text-white text-xl font-bold ${
                              selectedCollection.name === 'All' ? 'bg-gradient-to-br from-purple-600 to-purple-700 ring-2 ring-purple-300' : ''
                            }`}
                            style={selectedCollection.name === 'All' ? {} : { backgroundColor: selectedCollection.color || '#A78BFA' }}
                          >
                            {(selectedCollection.name || '?').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h2 className={`font-semibold ${selectedCollection.name === 'All' ? 'text-purple-900' : 'text-gray-900'}`}>
                              {selectedCollection.name || 'Unnamed Collection'}
                            </h2>
                            <p className={`text-sm ${selectedCollection.name === 'All' ? 'text-purple-700' : 'text-gray-600'}`}>
                              {selectedCollection.content_count || 0} items
                            </p>
                          </div>
                        </div>
                        {/* Bulk Select Button */}
                        <button
                          onClick={() => {
                            setBulkSelectMode(!bulkSelectMode);
                            if (bulkSelectMode) {
                              setSelectedItems(new Set());
                              setShowBulkCollectionSelector(false);
                            }
                          }}
                          className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
                            bulkSelectMode
                              ? 'bg-purple-600 text-white hover:bg-purple-700'
                              : 'bg-white/80 text-neutral-700 hover:bg-white border border-neutral-200'
                          }`}
                        >
                          {bulkSelectMode ? (
                            <>
                              <X size={16} />
                              Cancel
                            </>
                          ) : (
                            <>
                              <CheckSquare size={16} />
                              Bulk Select
                            </>
                          )}
                        </button>
                      </div>
                    );
                  })()}
                </div>
                
                {/* Bulk Collection Change UI */}
                {bulkSelectMode && selectedItems.size > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-xl"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-purple-900">
                          {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
                        </span>
                        <button
                          onClick={() => {
                            setSelectedItems(new Set());
                            setShowBulkCollectionSelector(false);
                          }}
                          className="text-xs text-purple-600 hover:text-purple-800"
                        >
                          Clear
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={showBulkCollectionSelector ? 'change' : ''}
                          onChange={(e) => {
                            if (e.target.value && e.target.value !== 'change') {
                              handleBulkChangeCollection(e.target.value);
                            } else {
                              setShowBulkCollectionSelector(true);
                            }
                          }}
                          className="px-3 py-1.5 text-sm border border-purple-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                          onClick={(e) => {
                            if (!showBulkCollectionSelector) {
                              e.preventDefault();
                              setShowBulkCollectionSelector(true);
                            }
                          }}
                        >
                          <option value="">Move to collection...</option>
                          {collections.map((collection) => (
                            <option key={collection.id} value={collection.id}>
                              {collection.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </motion.div>
                )}
                
                {/* Collection items - show loading if loading OR items are empty (to prevent showing stale content) */}
                {loading || items.length === 0 ? (
                  <div className="mb-6 text-neutral-500 text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A47F5] mx-auto mb-4"></div>
                    <p>Loading collection items…</p>
                  </div>
                ) : items && items.length > 0 ? (
                  <div>
                    <MasonryGrid 
                      items={items} 
                      onItemClick={onItemClick} 
                      onItemDelete={onItemDelete}
                      isBulkSelectMode={bulkSelectMode}
                      selectedItems={selectedItems}
                      searchMeta={searchMeta}
                      onBulkSelect={(itemId, selected) => {
                        setSelectedItems(prev => {
                          const next = new Set(prev);
                          if (selected) {
                            next.add(itemId);
                          } else {
                            next.delete(itemId);
                          }
                          return next;
                        });
                      }}
                    />
                  </div>
                ) : (
                  <div className="text-center py-12 text-neutral-500">
                    <p className="text-lg mb-2">No items in this collection.</p>
                    <p>Add content to this collection to see it here.</p>
                  </div>
                )}
              </div>
            ) : (
              // Show collection list when no collection is selected
              loadingCollections ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="w-6 h-6 animate-spin text-neutral-400" />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {/* Empty "+" Stack */}
                  <motion.button
                    onClick={() => setShowCreateModal(true)}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    className="bg-white rounded-lg border border-dashed border-gray-300 p-6 hover:border-gray-400 hover:bg-gray-50 transition-all flex flex-col items-center justify-center min-h-[100px] text-gray-500 hover:text-gray-700"
                  >
                    <Plus className="w-6 h-6 mb-2" />
                    <span className="text-xs font-medium">New Collection</span>
                  </motion.button>

                  {/* Collection Stacks */}
                  {collections.filter(c => c && c.id).map((collection) => (
                    <motion.div
                      key={collection.id}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className={`relative rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:border-gray-300 hover:shadow-md transition-all cursor-pointer group ${
                        selectedCollectionId === collection.id ? 'border-gray-900 shadow-md ring-1 ring-gray-900' : ''
                      }`}
                      style={{ minHeight: '140px' }}
                      onClick={() => handleCollectionSelect(collection)}
                    >
                      {/* Background Image */}
                      {collection.recent_thumbnail ? (
                        <div className="absolute inset-0">
                          <img
                            src={collection.recent_thumbnail}
                            alt={collection.name}
                            className="w-full h-full object-cover transition-opacity duration-300"
                            onLoad={(e) => {
                              e.target.style.opacity = '1';
                            }}
                            onError={(e) => {
                              // Hide broken image and show gradient
                              e.target.style.display = 'none';
                              const gradient = e.target.parentElement.nextElementSibling;
                              if (gradient) {
                                gradient.style.display = 'block';
                              }
                            }}
                            style={{ opacity: 0 }}
                          />
                        </div>
                      ) : null}
                      {/* Gradient fallback - always present but hidden when image loads */}
                      <div 
                        className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200"
                        style={{ display: collection.recent_thumbnail ? 'none' : 'block' }}
                      />
                      
                      {/* Dark Overlay */}
                      <div className="absolute inset-0 bg-black/50 group-hover:bg-black/60 transition-colors" />
                      
                      {/* Content */}
                      <div className="relative z-10 p-4 h-full flex flex-col justify-between">
                        {/* Top: Delete Button */}
                        <div className="flex justify-end">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCollection(collection, e);
                            }}
                            className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded transition-all opacity-0 group-hover:opacity-100"
                            title="Delete collection"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        
                        {/* Bottom: Title and Count */}
                        <div>
                          <h3 className="font-medium text-white text-sm mb-1 drop-shadow-lg">
                            {collection.name || 'Unnamed Collection'}
                          </h3>
                          <p className="text-xs text-white/80 drop-shadow">
                            {collection.content_count || 0} items
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )
            )}
          </div>
        ) : null}
      </AnimatePresence>

      {/* Microcopy */}
      <div className="mt-8 pt-6 border-t border-neutral-200/50">
        <p className="text-xs text-neutral-400 text-center" style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px' }}>
          Your space evolves with you — organize everything in Collections.
        </p>
      </div>

      {/* Delete Collection Confirmation Modal */}
      {showDeleteModal && collectionToDelete && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full"
          >
            <h3 className="text-lg font-semibold text-neutral-800 mb-2">
              Delete Collection
            </h3>
            <p className="text-sm text-neutral-600 mb-4">
              What would you like to do with "{collectionToDelete.name}"?
            </p>
            
            {/* Delete Mode Selection */}
            <div className="mb-4 space-y-2">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-neutral-50">
                <input
                  type="radio"
                  name="deleteMode"
                  value="collection"
                  checked={deleteMode === 'collection'}
                  onChange={(e) => setDeleteMode(e.target.value)}
                  className="mr-2"
                />
                <div>
                  <div className="font-medium text-sm">Delete collection only</div>
                  <div className="text-xs text-neutral-500">Items will remain in your library</div>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-neutral-50">
                <input
                  type="radio"
                  name="deleteMode"
                  value="items"
                  checked={deleteMode === 'items'}
                  onChange={(e) => setDeleteMode(e.target.value)}
                  className="mr-2"
                />
                <div>
                  <div className="font-medium text-sm">Delete collection and all items</div>
                  <div className="text-xs text-neutral-500">
                    This will permanently delete {collectionToDelete.content_count || 0} items
                  </div>
                </div>
              </label>
            </div>
            
            {/* Confirmation Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Type <strong className="text-red-600">delete</strong> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'delete' to confirm"
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                autoFocus
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setCollectionToDelete(null);
                  setDeleteConfirmText('');
                }}
                className="px-4 py-2 text-neutral-600 hover:text-neutral-800 transition-colors"
                disabled={deletingCollection}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteCollection}
                disabled={!deleteConfirmText.trim() || deletingCollection || deleteConfirmText.trim().toLowerCase() !== 'delete'}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deletingCollection && <Loader className="w-4 h-4 animate-spin" />}
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Create Collection Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full"
          >
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">
              Name your new Collection
            </h3>
            <input
              type="text"
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e.target.value)}
              placeholder="Collection name"
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-purple-500"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newCollectionName.trim()) {
                  handleCreateCollection();
                } else if (e.key === 'Escape') {
                  setShowCreateModal(false);
                }
              }}
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewCollectionName('');
                }}
                className="px-4 py-2 text-neutral-600 hover:text-neutral-800 transition-colors"
                disabled={creatingCollection}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCollection}
                disabled={!newCollectionName.trim() || creatingCollection}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {creatingCollection && <Loader className="w-4 h-4 animate-spin" />}
                Create
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

